/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import "./summary-field.js";

import ko from "knockout";
import birdata from "../api.js";
import util from "../util/util.js";
import moment from "dayjs";
import _ from "underscore";
import programsPromise from "../programs.js";
import googleMaps from "../util/google-maps.js";
import mapStyles from "../map/styles.js";
import mapFeatures from "../map/features.js";
import extensionsPromise from "../extensions.js";
import { showGalleryInShadowDom } from "../util/gallery.js";

function observePromise(p) {
  var o = ko.observable();
  p.then(o);
  return o;
}


var SurveySummaryViewModel = function(params) {
	var self = this;

  var survey = params.survey;
  var json = params.surveyJSON;

  var lookups = observePromise(birdata.lookup());
  var programs = observePromise(programsPromise());
  var species = observePromise(birdata.speciesList({ supertaxa: "all" }));
  var extensions = observePromise(extensionsPromise());

  var visible = params.visible ?
    params.visible.extend({ rateLimit: { method: "notifyWhenChangesStop", timeout: 400 } }) :
    ko.observable(true);

  self.recordingSurvey = !!survey;

  self.showProgramFields = ko.pureComputed(function() {
    if(survey) return true;
    if(json && json()) return json().completed;
  });

  var surveyPoint = ko.pureComputed(function() {
    if(survey) return survey.surveyPoint();
    if(json && json()) return json().survey_point;
  });//.extend({stringify: true});

  self.isRecordingSurvey = !!params.survey;

  self.hasSurveyPoint = ko.pureComputed(function() {
    return !!surveyPoint();
  });

  self.mapImageURL = ko.pureComputed(function() {
    var sp = surveyPoint();
    if(sp) {
      return mapImageURL(sp.lat, sp.lon);
    }
  });

  self.googleMapsURL = ko.pureComputed(function() {
    var sp = surveyPoint();
    if(sp) {
      return "https://www.google.com/maps/search/?api=1&query=" + sp.lat + "," + sp.lon;
    }
  });

  self.surveyPointDescription = ko.pureComputed(function() {
    var sp = surveyPoint();
    if(sp) {
      return ko.unwrap(sp.name) || '(Unnamed)';
    }
  });

  self.lat = ko.pureComputed(function() {
    if(surveyPoint()) {
      return surveyPoint().lat.toFixed(7) + " (" + util.formatDMS(surveyPoint().lat, "N", "S") + ")";
    }
  });

  self.lon = ko.pureComputed(function() {
    if(surveyPoint()) {
      return surveyPoint().lon.toFixed(7) + " (" + util.formatDMS(surveyPoint().lon, "E", "W") + ")";
    }
  });

  self.routeInfo = ko.pureComputed(function() {
    if(survey) { return survey.program().routeBasedEntry && survey.routeInfo(); }
    if(json && json()) {
      var program = _.findWhere(programs() || [], { code: json().program });
      if(program && program.routeBasedEntry && json()[program.code]) {
        var j = json()[program.code];
        var route = {
          start: j.start_lat ? { lat: j.start_lat, lon: j.start_lon } : null,
          finish: j.finish_lat ? { lat: j.finish_lat, lon: j.finish_lon } : null,
          type: j.shared_site_id ? 'existing' : 'ad-hoc',
          sharedSite: ko.observable()
        };
        if(j.shared_site_id) {
          birdata.sharedSite(j.shared_site_id).then(route.sharedSite);
        }
        return route;
      }
    }
  });

  self.programInfo = ko.pureComputed(function() {
    if(survey) {
      return survey.program() || {};
    } else if(json && json()) {
      return _.findWhere(programs() || [], { code: json().program }) || {};
    } else {
      return {};
    }
  });

  self.showNewOrExistingSurveyPoint = ko.pureComputed(function() {
    return self.isRecordingSurvey && !(survey && survey.program() && survey.program().siteBasedEntry);
  });

  self.isExistingSurveyPoint = ko.pureComputed(function() {
    var sp = surveyPoint();
    return sp && !!sp.id;
  });

  function accuracyString(acc) {
    if(acc === null || acc === undefined) {
      return "Not recorded";
    } else if(acc <= 100) {
      return "Within survey area";
    } else if(acc < 1000) {
      return "Within " + acc + "m of survey area";
    } else if(acc == 1000) {
      return "Within 1km of survey area";
    } else {
      return "> 1km from survey area";
    }
  }

  self.surveyPointAccuracy = ko.pureComputed(function() {
    var sp = surveyPoint();
    if(sp) {
      if(self.isExistingSurveyPoint()) {
        return accuracyString(sp.accuracy_in_metres);
      } else {
        return sp.accuracy && sp.accuracy() && sp.accuracy().value;
      }
    }
  });

  self.sharedSites = ko.observable([]);
  autoDispose(ko.computed(function() {
    var sp = surveyPoint();
    if(sp) {
      birdata.sharedSitesAtPoint(sp.lat, sp.lon).then(self.sharedSites);
    }
  }));

  self.startDate = ko.pureComputed(function() {
    if(survey) return moment(survey.startDate()).format("DD/MM/YYYY");
    if(json && json()) return moment(json().start_date).format("DD/MM/YYYY");
  });

  self.finishDate = ko.pureComputed(function() {
    if(survey) return moment(survey.finishDate()).format("DD/MM/YYYY");
    if(json && json()) return moment(json().finish_date).format("DD/MM/YYYY");
  });

  self.startTime = ko.pureComputed(function() {
    if(survey) return survey.startTime();
    if(json && json()) return json().start_time;
  });

  self.multipleDays = ko.pureComputed(function() {
    if(survey) return survey.multipleDays();
    if(json && json()) return !!json().finish_date;
  });

  self.duration = ko.pureComputed(function() {
    var m;
    if(survey) m = ko.unwrap(survey).durationInMinutes();
    else if(json && json()) m = json().duration_in_minutes;

    return formatDuration(m);
  });

  self.surveyTypeDescription = ko.pureComputed(function() {
    if(survey) {
      var s = ko.unwrap(survey);
      var type = ko.unwrap(s.type);
      return type ? type.name : "Not selected";
    }

    if(json && json()) return lookupName('survey_type', json().survey_type);
  });

  self.waterLevel = ko.pureComputed(function() {
    if(survey) {
      var w = ko.unwrap(survey).waterLevel();
      return w ? w.name : '';
    }
    if(json && json()) return lookupName('water_level', json().water_level);
  });

  self.observerCount = ko.pureComputed(function() {
    if(survey) return survey.observerCount();
    if(json && json()) return json().observer_count;
  });

  self.allSpeciesRecorded = ko.pureComputed(function() {
    if(survey) return survey.allSpeciesRecorded();
    if(json && json()) return !!json().all_species_recorded;
  });

  self.notes = ko.pureComputed(function() {
    if(survey) return survey.notes();
    if(json && json()) return json().notes;
  });

  self.program = ko.pureComputed(function() {
    if(survey) return survey.program().code;
    if(json && json()) return json().program;
  });

  self.isPrivate = ko.pureComputed(function() {
    if(survey) return survey.isPrivate();
    if(json && json()) return json().is_private;
  });

  self.observerName = ko.pureComputed(function() {
    if(params.showObserverName && json && json()) return json().observer_name;
  });

  self.observerEmail = ko.pureComputed(function() {
    if(params.showObserverName && json && json()) return json().observer_email;
  });

  self.isEmbedded = ko.pureComputed(function() {
    if(survey) return survey.isEmbedded();
    if(json && json()) return false;
  });

  self.unsortedSightings = autoDispose(ko.computed(function() {
    if(survey) return _.filter(survey.sightings(), function(s) {
      return s.isPresent();
    });
    if(json && json() && json().sightings && species() && programs()) {
      var program = _.findWhere(programs() || [], { code: json().program });

      program?.preprocessJSON?.(json());

      var speciesCounters = {}; // used to number species in sighting list

      var sightings = _.map(json().sightings || [], function(s, index) {
        var sp = s.species ??  _.findWhere(species(), { id: s.species_id });

        if(sp == null) {
          return null;
        }

        var title = sp.common_name;

        if(program.multipleSightingsPerSpecies) {
          speciesCounters[sp.id] = (speciesCounters[sp.id] || 0) + 1;
          title = title + " #" + speciesCounters[sp.id];
        }

        var sighting = {
          species: sp,
          title: title,
          count: ko.observable(s.count),
          breeding: ko.observable(lookups() ? lookups().find('breeding_activity', s.breeding_activity) : null),
          notes: s.notes,
          media: _.map(s.media_uuids || [], function(uuid) {
            return {
              uuid: ko.observable(uuid),
              url: ko.observable(birdata.uploadURL(uuid)),
              thumbUrl: ko.observable(birdata.uploadURL(uuid) + "/thumb")
            };
          }),
          location: ko.observable(s.location),
          programFields: ko.observable({}),
          index: index
        };

        // --- Program specific sighting fields
        ko.ignoreDependencies(function() {
          if(program && program.sightingFields) {
            var fields = program.sightingFields(sighting);
            _.each(fields, function(f) {
              f.field.load(s[program.code]);
            });
            sighting.programFields()[program.code] = fields;
            sighting.programFields.valueHasMutated();
          }
        });
        // ---

        return sighting;
      });

      return sightings.filter(s => s != null);
    }
  }));

  self.sightingCount = ko.pureComputed(function() {
    if(json && json() && !json().sightings) {
      return null;
    } else {
      var sightings = self.unsortedSightings();
      return sightings ? sightings.length : null;
    }
  });

  self.sightings = ko.pureComputed(function() {
    var unsortedSightings = self.unsortedSightings();
    if(unsortedSightings !== undefined) {
      return _.sortBy(unsortedSightings, function(s) { return s.species.sort; });
    }
  });

  self.totalCount = ko.pureComputed(function() {
    if(!self.sightingsLoaded()) return 0;
    var sum = 0;
    _.each(self.sightings(), function(s) {
      sum += (+s.count() || 0);
    });
    return sum;
  });

  self.totalCountOutside = ko.pureComputed(function() {
    if(!self.sightingsLoaded()) return 0;
    if(!self.isEmbedded()) return 0;
    var sumInside = 0;
    var sumOutside = 0;
    _.each(self.sightings(), function(s) {
      sumOutside += (+s.countOutside() || 0);
      if(s.countOutside() !== 'P') {
        // Inside count is ignored for total if outside is presence-only
        sumInside += (+s.count() || 0);
      }
    });

    return sumInside + " + " + sumOutside + " = " + (sumInside + sumOutside);
  });

  self.showTotals = ko.pureComputed(function() {
    return self.hasSightings() && self.isRecordingSurvey;
  });

  self.sightingsLoaded = ko.pureComputed(function() {
    return self.sightings() != undefined;
  });

  self.hasSightings = ko.pureComputed(function() {
    return self.sightingsLoaded() && self.sightings().length > 0;
  });

  self.sensitiveSightingsHidden = ko.pureComputed(function() {
    if(json) {
      console.log(json());
    }
    return !!(json && json() && json().sensitive_sightings_hidden);
  })

  self.programFields = function() {
    if(survey) {
      var data = survey.programData()[self.program()];
      if(data) {
        return data.fields;
      }
    }

    if(json) {
      var program = _.findWhere(programs() || [], { code: json().program });

      if(program) {
        var programFields;

        ko.ignoreDependencies(function() {
          programFields = program.fields();
          program.loadFromJSON(json(), programFields, null);
        });


        return programFields;
      }
    }
  };

  self.extensionData = ko.pureComputed(function() {
    if(survey) {
      return survey.extensionData;
    } else if(json && json()) {
      var extensionData = {};
      if(extensions()) { // Make sure extensions have loaded
        ko.ignoreDependencies(function() {
          (extensions()).forEach(extension => {
            var fields = extension.fields();
            extensionData[extension.code] = {fields: fields};
            extension.loadFromJSON(json(), fields, null);
          });
        });
      }
      return extensionData;
    } else {
      return {};
    }
  });

  // Custom BIBY code

  function speciesObservable(id) {
    var obs = ko.observable();
    birdata.speciesList().then(function(speciesList) {
      obs(_.findWhere(speciesList, {id: id}));
    });
    return obs;
  }

  self.bibyInteractions = ko.pureComputed(function() {
    if(survey && survey.program().code == 'biby') {
      return _.flatten(_.map(survey.sightings(), function(sighting) {
        return (sighting.isPresent() && sighting.bibyInteraction.interactionObserved()) ? [sighting.bibyInteraction] : [];
      }));
    }

    if(json && json() && json().program == 'biby' && json().biby) {
      return _.map(json().biby.interactions, function(ji) {
        return {
          interactionObserved: ko.observable(true),
          aggressive: ko.observable(ji.aggressive),
          initiatorSpecies: speciesObservable(ji.initiator_species_id),
          targetSpecies: speciesObservable(ji.target_species_id),
          possibleReason: ko.observable(lookups() ? lookups().find('biby_interaction_reason', ji.possible_reason) : null),
          outcome: ko.observable(lookups() ? lookups().find('biby_interaction_outcome', ji.outcome) : null)
        };
      });
    }

    return [];
  });

  // End custom BIBY code

  function lookupName(lookup, code) {
    if(lookups()) {
      var item = lookups().find(lookup, code);
      if(item) return item.name;
    }
    return '';
  }

  function formatDuration(m) {
    var h = Math.floor(m / 60);
    m = m % 60;

    var result = [];

    if(h > 0) {
      result.push(h == 1 ? "1 hour" : h + " hours");
    }

    if(m > 0) {
      result.push(m == 1 ? "1 minute" : m + " minutes");
    }
    return result.join(" ");
  }

  function mapImageURL(lat, lon) {
    var coords = lat + "," + lon;

    return googleMaps.staticMapURL({
      center: coords,
      zoom: 13,
      size: '400x300',
      maptype: 'terrain',
      markers: 'color:red|' + coords,
      scale: 2
    });
  }

  // ---- Interactive map for surveys with sighting locations

  var googleMapsAvailable = ko.observable(false);
  googleMaps.load().then(function() {
    googleMapsAvailable(true);
  });

  self.showInteractiveMap = ko.pureComputed(function() {
    if(json && json()) {
      var program = _.findWhere(programs() || [], { code: json().program });
      return !!(program && program.sightingLocations);
    } else if(survey && googleMapsAvailable()) {
      return survey.program() && survey.program().sightingLocations && !surveyBounds().isEmpty();
    }
  });

  self.map = ko.observable();

  self.mapOptions = ko.observable();

  function ll(p) {
    return new google.maps.LatLng(p.lat, p.lon);
  }

  function surveyBounds() {
    var bounds = new google.maps.LatLngBounds();

    var sp = surveyPoint();
    if(sp) {
      bounds.extend(ll(surveyPoint()));
    }

    var route = self.routeInfo();
    if(route) {
      if(route.start) {
        bounds.extend(ll(route.start));
      }
      if(route.finish) {
        bounds.extend(ll(route.finish));
      }

      var site = ko.unwrap(route.sharedSite);
      if(site) {
        bounds = bounds.union({
          east: site.bounds.maxx,
          west: site.bounds.minx,
          north: site.bounds.maxy,
          south: site.bounds.miny
        });
      }
    }
    (self.sightings() || []).forEach(function(s) {
      if(s.location()) {
        bounds.extend(ll(s.location()));
      }
    });
    return bounds;
  }

  // Fit map to bounds
  autoDispose(ko.computed(function() {
    var map = self.map();
    if(map && visible()) {
      var bounds = surveyBounds();
      if(bounds.toSpan().lat() > 0 && bounds.toSpan().lng() > 0) {
        map.fitBounds(bounds, 20);
      }
    }
  }));

  autoDispose(ko.computed(function() {
    if(self.showInteractiveMap()) {
      ko.ignoreDependencies(function() {
        googleMaps.load().then(function() {
          self.mapOptions(googleMaps.defaultOptions({
            center: surveyBounds().getCenter(),
            zoom: 16,
            gestureHandling: "cooperative",
            fullscreenControl: false
          }));
        });
      });
    }
  }));

  var sightingMarkers = [];

  autoDispose(ko.computed(function() {
    var map = self.map();
    var sightings = self.unsortedSightings();

    if(map && sightings && visible()) {
      sightingMarkers.forEach(m => m.setMap(null));
      sightingMarkers = sightings.filter(s => s.location()).map(function(s) {
        var marker = new google.maps.Marker({
          map: map,
          position: ll(s.location()),
          icon: mapStyles.surveyIcon(),
          title: ko.unwrap(s.title)
        });
        google.maps.event.addListener(marker, "click", function() {
          var els = document.querySelectorAll("[data-index='" + s.index + "']");
          if(els.length > 0) {
            els[0].scrollIntoView();
          }
        });
        return marker;
      });
    }
  }));

  // Draw route markers
  var routeMarkers = [];
  autoDispose(ko.computed(function() {
    var map = self.map();
    var route = self.routeInfo();
    if(map && visible() && route) {
      routeMarkers.forEach(m => m.setMap(null));
      if(route.start) {
        routeMarkers.push(mapStyles.endpointMarker(route.start, true, map));
      }
      if(route.finish) {
        routeMarkers.push(mapStyles.endpointMarker(route.finish, false, map));
      }
    }
  }));

  // Draw route polygon
  var lastSite = null;
  autoDispose(ko.computed(function() {
    var map = self.map();
    var route = self.routeInfo();
    if(map && visible() && route) {
      var site = ko.unwrap(route.sharedSite);
      if(site) {
        if(site === lastSite) {
          return;
        }
        lastSite = site;
        if(site.geom) {
          addSiteToMap(site.geom, map);
        } else {
          birdata.sharedSite(site.id).then(function(fullSite) {
            addSiteToMap(fullSite.geom, map);
          });
        }
      }
    }
  }));

  var sitePolygons = [];
  function addSiteToMap(siteGeometry, map) {
    sitePolygons.forEach(function(p) {
      p.setMap(null);
    });
    sitePolygons = mapFeatures.addFeatureToMap(siteGeometry, map, mapStyles.sharedSitePolygonStyle());
  }

  // Draw route track
  autoDispose(ko.computed(function() {
    var map = self.map();
    var routeGeoJson = json?.()?.route_track;
    if(map && routeGeoJson) {
      let lines = routeGeoJson.coordinates.map(polylineFromGeoJSONCoordinates);
      lines.forEach(l => l.setMap(map));
    }
  }));

  function polylineFromGeoJSONCoordinates(line) {
    return new google.maps.Polyline({
      path: line.map(([x,y]) => ({ lat: y, lng: x })),
      geodesic: true,
      strokeColor: "#FF0000",
      strokeOpacity: 1.0,
      strokeWeight: 2
    })
  }

  // --- End interactive map

  self.speciesURL = function(species) {
    return birdata.speciesImageURL(species.id, 'small_square');
  };

  // --- Disposal logic

  var disposables;
  self.dispose = function() {
    _.each(disposables, function(d) { d.dispose(); });
  };
  function autoDispose(d) {
    if(!disposables) disposables = [];
    disposables.push(d);
    return d;
  }
};

// Simple image gallery solution
document.addEventListener("click", function(evt) {
  if(evt.target.matches(".survey-summary .attachment-thumbnail")) {
    let summary = evt.target.closest(".survey-summary");
    let images = Array.from(summary.querySelectorAll(".attachment-thumbnail"));
    images = images.map((el) => ({
        src: el.parentElement.href,
        caption: ""
    })).filter(x => x.src);

    let currentURL = evt.target.parentElement.href;
    let currentIndex = images.findIndex(x => x.src == currentURL);
    if(images.length > 0) {
      showGalleryInShadowDom(images, currentIndex >= 0 ? currentIndex : 0);
    }
    evt.preventDefault();
    return true;
  }
});

ko.components.register('survey-summary', {
	viewModel: SurveySummaryViewModel,
	template: require('../../templates/survey-summary.html')
});

export default SurveySummaryViewModel;
